import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMyCampaigns } from "../api/campaigns";
import { SELECTED_TAB } from "../components/campaign/shared";
import { CampaignTile } from "../components/discover/DiscoverAll";
import AppHeader from "../components/ds/AppHeader";
import EmptyState from "../components/ds/EmptyState";
import SearchField from "../components/ds/SearchField";
import SegmentedControl from "../components/ds/SegmentedControl";
import { useTranslation } from "../context/TranslationContext";
import { getCampaignDetailsPath } from "../utils/campaignCardUtils";

/** Figma V6.3: the campaigns running in this space, for creators to join. */
const MintSpaceCampaignsDiscoverPage = ({ setToast, spaceId, spaceName }) => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [type, setType] = useState("all");

  useEffect(() => {
    if (!spaceId) return;
    setLoading(true);
    getMyCampaigns("", spaceId, true)
      .then((res) => setCampaigns((res?.data || []).filter((campaign) => campaign.status === "active")))
      .catch(() => setToast?.(t("campaigns.failedToLoadCampaigns")))
      .finally(() => setLoading(false));
  }, [spaceId, setToast, t]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return campaigns.filter(
      (campaign) =>
        (type === "all" || campaign.participantType === type || campaign.participantType === "both") &&
        (!q || (campaign.campaignName || "").toLowerCase().includes(q))
    );
  }, [campaigns, query, type]);

  return (
    <div className="flex flex-col gap-space-24 font-ds">
      <AppHeader
        app="creator-rewards"
        eyebrow="Creator Rewards"
        title={t("campaignFlow.campaignsInSpace", { space: spaceName })}
        subtitle="Creator Rewards"
      />
      <div className="flex flex-col gap-space-12 lg:flex-row lg:items-center">
        <SearchField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("campaignFlow.searchCampaigns")}
          className="!h-[40px] min-w-0 flex-1"
        />
        <SegmentedControl
          track="material"
          label={t("navigation.creatorRewards")}
          value={type}
          onChange={setType}
          selectedClass={SELECTED_TAB}
          className="shrink-0 [&>button]:px-space-8 lg:w-[320px]"
          options={[
            { value: "all", label: t("common.all") },
            { value: "creators", label: t("campaigns.ugc") },
            { value: "clippers", label: t("campaigns.clipping") },
          ]}
        />
      </div>
      {loading ? (
        <p className="py-space-40 text-center text-subheadline text-label-secondary">{t("campaigns.loadingCampaigns")}</p>
      ) : shown.length === 0 ? (
        <div className="rounded-radius-lg bg-bg-raised">
          <EmptyState icon="gift" title={t("campaigns.noCampaigns")} message={t("campaigns.noCampaignsAvailable")} />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-space-16 min-[700px]:grid-cols-2 lg:gap-space-24 min-[1200px]:grid-cols-3">
          {shown.map((campaign) => (
            <CampaignTile
              key={campaign._id}
              campaign={campaign}
              onOpen={(item) => navigate(getCampaignDetailsPath(item))}
              language={language}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MintSpaceCampaignsDiscoverPage;
